/**
 * Format a number using the Indian number system
 * 1000 → 1,000
 * 10000 → 10,000
 * 100000 → 1,00,000
 * 1000000 → 10,00,000
 */
export function formatIndian(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value as number)) return '0';
  
  const num = Number(value);
  const isNegative = num < 0;
  const absNum = Math.abs(num);
  
  // Split into integer and decimal parts
  const [intPart, decPart] = absNum.toString().split('.');
  
  // Apply Indian grouping: last 3 digits, then groups of 2
  const lastThree = intPart.slice(-3);
  const remaining = intPart.slice(0, -3);
  
  let formatted = lastThree;
  if (remaining) {
    // Group remaining digits in pairs from right
    const pairs: string[] = [];
    for (let i = remaining.length; i > 0; i -= 2) {
      pairs.unshift(remaining.slice(Math.max(0, i - 2), i));
    }
    formatted = pairs.join(',') + ',' + lastThree;
  }
  
  const result = decPart ? `${formatted}.${decPart}` : formatted;
  return isNegative ? `-${result}` : result;
}

/**
 * Format a currency value with ₹ prefix in Indian number system
 * 375 → ₹375.00
 * 100000 → ₹1,00,000.00
 */
export function formatCurrency(value: number | null | undefined, decimals = 2): string {
  if (value === null || value === undefined) return '—';
  const num = Number(value);
  if (isNaN(num)) return '—';
  const fixed = num.toFixed(decimals);
  const [intPart, decPart] = fixed.split('.');
  const formatted = formatIndian(parseInt(intPart, 10));
  return decPart ? `₹${formatted}.${decPart}` : `₹${formatted}`;
}

/**
 * Compact format for large numbers (for dashboard tiles)
 * 1234567 → 12.3L  (Lakhs)
 * 9876543210 → 9.9Cr (Crores)
 */
export function formatCompact(value: number | null | undefined): string {
  if (value === null || value === undefined) return '0';
  const num = Number(value);
  if (num >= 10000000) return `${(num / 10000000).toFixed(1)}Cr`;
  if (num >= 100000) return `${(num / 100000).toFixed(1)}L`;
  if (num >= 1000) return formatIndian(Math.round(num));
  return String(num);
}
